import api from "@/api";
import details from "@/store/modules/product/details.json";
import { getItemsAndQuantityInCart } from "@/util.js";

const getProductModifiers = (storeModifiers, productModifiers) => {
  // use group modifier ids on product to build full collection from store
  let modifierGroups = productModifiers.reduce((acc, groupModifierId) => {
    let foundModifier = storeModifiers.find(
      (sm) => sm.modifier_group === groupModifierId,
    );
    if (foundModifier) {
      acc.push({ ...foundModifier, valid: true });
    }
    return acc;
  }, []);
  return modifierGroups;
};

const state = () => ({
  details,
  dialog: {
    details: false,
    modifiers: false,
    addFromCart: false,
  },
  noProductFound: false,
});

const actions = {
  editModifier({ commit, rootState }, product) {
    const { info } = rootState.store;

    // product already has modifierGroups when added on client, modifier_groups, modifiers array and modifier_group on each modifier

    const modifierGroups = getProductModifiers(
      info.modifierGroups,
      product.modifier_groups,
    );

    const p = !product.modifierGroups
      ? { ...product, modifierGroups }
      : product;

    // these p modifiers need modifier_group to work on each

    const modifierGroupsArr = Object.values(modifierGroups);
    let modifiersWithModGroup = [];

    p.modifiers.forEach((modifier) => {
      modifierGroupsArr.forEach((modifierGroup) => {
        const some = modifierGroup.modifiers.some(
          (_) =>
            _.upc === modifier.upc &&
            modifier.modifier_group === modifierGroup.modifier_group,
        );
        if (some) {
          modifiersWithModGroup.push({
            ...modifier,
            modifier_group: modifierGroup.modifier_group,
          });
        }
      });
    });
    commit("SET_CURRENT_PRODUCT", { ...p, modifiers: modifiersWithModGroup });
  },
  async getProduct({ commit, dispatch }, upc) {
    const data = await api({ url: `/123/products/${upc}` });
    // a non-product returns an empty array on data.info
    data.err
      ? dispatch("error/showError", data.err, { root: true })
      : data.info.upc
      ? dispatch("product/setCurrentProductDetails", data.info, { root: true })
      : commit("PRODUCT_NOT_FOUND", true);
  },
  openDialog({ commit }, view) {
    commit("OPEN_DIALOG", view);
  },
  async handleModifierSelect({ commit }, { checked, modifier }) {
    commit("HANDLE_MODIFIER_SELECT", { checked, modifier });
  },
  async setCurrentProductDetails({ commit, rootState }, product) {
    commit("PRODUCT_NOT_FOUND", false);
    if (!product.modifier_groups) {
      commit("SET_CURRENT_PRODUCT", product);
    } else {
      const { info } = rootState.store;
      const { products } = rootState.cart;
      const modifierGroups = getProductModifiers(
        info.modifierGroups,
        product.modifier_groups,
      );
      const productWithModifiers = {
        ...product,
        modifierGroups,
        modifiers: [],
      };
      const cartItemsAndQty = getItemsAndQuantityInCart(products, product);
      commit("SET_CURRENT_PRODUCT", {
        ...details,
        ...productWithModifiers,
        ...cartItemsAndQty,
      });
    }
  },
};

const mutations = {
  CLOSE_DIALOG(state, dialog) {
    state.dialog[dialog] = false;
  },
  HANDLE_MODIFIER_SELECT(state, { checked, modifier }) {
    state.details.modifiers = checked
      ? [...state.details.modifiers, modifier]
      : state.details.modifiers.filter(
          (_) =>
            modifier.upc !== _.upc ||
            modifier.modifier_group !== _.modifier_group,
        );
  },
  INITIALIZE_QTY(state) {
    const { details } = state;
    state.details.qty = details.item_id
      ? details.qty
      : details.byweight
      ? 0.5
      : 1;
  },
  LESS_QTY(state) {
    const { details } = state;
    const q = parseFloat(details.qty);
    state.details.qty = details.byweight ? q - 0.5 : q - 1;
  },
  MORE_QTY(state) {
    const { details } = state;
    const q = parseFloat(details.qty);
    state.details.qty = details.byweight ? q + 0.5 : q + 1;
  },
  OPEN_DIALOG(state, dialog) {
    state.dialog[dialog] = true;
  },
  PRODUCT_NOT_FOUND(state, bool) {
    state.noProductFound = bool;
  },
  REMOVE_ALL_CART_ITEMS(state) {
    state.details.cartItems = [];
  },
  REMOVE_CART_ITEM(state, itemId) {
    state.details.cartItems = state.details.cartItems.filter(
      (cartItem) => cartItem.item_id !== itemId,
    );
  },
  SET_CURRENT_PRODUCT(state, product) {
    state.details = product;
  },
  UPDATE_PRODUCT_MODIFIER_GROUPS(state, modifierGroups) {
    state.details.modifierGroups = modifierGroups;
  },
};

const getters = {
  hasTieredDiscount: (state) =>
    state.details.pricebook_info.promotions?.find((p) => p.extension?.tiers),
  missingModifiers: (state) =>
    state.details.modifierGroups.some((group) => !group.valid),
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
